import { useState, useEffect, useRef } from 'react';

interface GenerationProgressProps {
  status: string;
  generationStep: number | null;
  generationStepName: string | null;
  isResolveEpisode: boolean;
  isProcessingFinalEvaluation: boolean;
  isLastEpisode: boolean;
}

/**
 * Smooth fake progress bar for the pre-selection phase.
 * Fills from 0 → ~90% over ~60s using an easing curve, then crawls slowly.
 * Resets when unmounted.
 */
function SmoothProgressBar({ message }: { message: string }) {
  const [percent, setPercent] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const tick = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      // Fast to ~70% in 30s, then slows to ~90% by 60s, then crawls
      let p: number;
      if (elapsed < 30) {
        p = (elapsed / 30) * 70;
      } else if (elapsed < 60) {
        p = 70 + ((elapsed - 30) / 30) * 20;
      } else {
        // Crawl from 90 towards 97, never reaching 100
        p = 90 + 7 * (1 - Math.exp(-(elapsed - 60) / 120));
      }
      setPercent(Math.min(Math.round(p), 97));
    };

    const id = setInterval(tick, 500);
    tick();
    return () => clearInterval(id);
  }, []);

  return (
    <div className="bg-white dark:bg-white/10 dark:backdrop-blur-md dark:border dark:border-white/15 rounded-2xl shadow-sm dark:shadow-none p-6 mb-8">
      <div className="text-center mb-5">
        <p className="text-lg font-medium text-dark dark:text-white">{message}</p>
      </div>

      <div className="mb-4">
        <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2.5">
          <div
            className="h-full bg-primary rounded-full transition-all duration-700 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      <div className="text-center mt-4">
        <p className="text-sm text-gray-500 dark:text-white/60">
          Aguarda um momento, já vamos mostrar as opções para o próximo episódio...
        </p>
      </div>
    </div>
  );
}

/**
 * Visual phase definitions.
 *
 * Backend steps (1-7):
 *   1 = npc_reaction        (completion flow)
 *   2 = narrative_update     (completion flow)
 *   3 = episode_options / final_evaluation (completion flow)
 *   4 = character_profile    (generation flow)
 *   5 = encounter_setup      (generation flow)
 *   6 = scenario_writing     (generation flow)
 *   7 = finishing            (generation flow)
 *
 * We group these into user-friendly phases.
 * When the user is in `preparing_selected_episode` (after choosing a candidate),
 * only the generation-flow phases are shown since 1-3 already happened.
 */
interface Phase {
  id: string;
  label: string;
  message: string;
  /** Technical generation steps that map to this phase */
  steps: number[];
}

const FULL_PHASES: Phase[] = [
  {
    id: 'analyze',
    label: 'Analisar conversa',
    message: 'A analisar como correu a conversa...',
    steps: [1, 2],
  },
  {
    id: 'discover',
    label: 'Descobrir personagens',
    message: 'A descobrir quem quer falar contigo a seguir...',
    steps: [3],
  },
  {
    id: 'profile',
    label: 'Conhecer personagem',
    message: 'A conhecer a personagem...',
    steps: [4],
  },
  {
    id: 'encounter',
    label: 'Planear encontro',
    message: 'A planear o encontro...',
    steps: [5],
  },
  {
    id: 'scenario',
    label: 'Escrever cenário',
    message: 'A escrever o cenário...',
    steps: [6],
  },
  {
    id: 'finish',
    label: 'Últimos retoques',
    message: 'Últimos retoques antes de começar...',
    steps: [7],
  },
];

/** When user already chose a candidate, we only show the generation-flow phases */
const SELECTED_PHASES: Phase[] = [
  {
    id: 'profile',
    label: 'Conhecer personagem',
    message: 'A conhecer a personagem...',
    steps: [4],
  },
  {
    id: 'encounter',
    label: 'Planear encontro',
    message: 'A planear o encontro...',
    steps: [5],
  },
  {
    id: 'scenario',
    label: 'Escrever cenário',
    message: 'A escrever o cenário...',
    steps: [6],
  },
  {
    id: 'finish',
    label: 'Últimos retoques',
    message: 'Últimos retoques antes de começar...',
    steps: [7],
  },
];

const RESOLVE_PHASES: Phase[] = [
  {
    id: 'analyze',
    label: 'Analisar conversa',
    message: 'A analisar como correu a conversa...',
    steps: [1, 2],
  },
  {
    id: 'encounter',
    label: 'Planear resolução',
    message: 'A planear a conversa de resolução...',
    steps: [3, 5],
  },
  {
    id: 'scenario',
    label: 'Escrever cenário',
    message: 'A escrever o cenário de resolução...',
    steps: [6],
  },
  {
    id: 'finish',
    label: 'Últimos retoques',
    message: 'Últimos retoques antes de começar...',
    steps: [7],
  },
];

const FINAL_EVAL_PHASES: Phase[] = [
  {
    id: 'analyze',
    label: 'Analisar conversa',
    message: 'A analisar como correu a conversa...',
    steps: [1, 2],
  },
  {
    id: 'evaluate',
    label: 'Preparar avaliação',
    message: 'A preparar a tua avaliação final...',
    steps: [3, 4, 5, 6, 7],
  },
];

function getPhaseStatus(phase: Phase, currentStep: number): 'completed' | 'active' | 'pending' {
  const maxStepInPhase = Math.max(...phase.steps);
  const minStepInPhase = Math.min(...phase.steps);

  if (currentStep > maxStepInPhase) return 'completed';
  if (currentStep >= minStepInPhase) return 'active';
  return 'pending';
}

export default function GenerationProgress({
  status,
  generationStep,
  isResolveEpisode,
  isProcessingFinalEvaluation,
  isLastEpisode,
}: GenerationProgressProps) {
  // Pre-selection phase: user is waiting for candidates to appear.
  // Show a single smooth progress bar instead of many segmented phases,
  // because from the user's perspective only one thing is happening.
  const isPreSelectionPhase = !isResolveEpisode && !isProcessingFinalEvaluation && !isLastEpisode
    && (status === 'processing_recap' || status === 'preparing_candidates' || status === 'generating');

  if (isPreSelectionPhase) {
    const message = status === 'processing_recap'
      ? 'A analisar como correu a conversa...'
      : 'A descobrir quem quer falar contigo a seguir...';
    return <SmoothProgressBar message={message} />;
  }

  // Choose which set of phases to display
  let phases: Phase[];
  if (isProcessingFinalEvaluation || (isLastEpisode && !isResolveEpisode)) {
    phases = FINAL_EVAL_PHASES;
  } else if (isResolveEpisode) {
    if (status === 'preparing_selected_episode') {
      phases = SELECTED_PHASES;
    } else {
      phases = RESOLVE_PHASES;
    }
  } else if (status === 'preparing_selected_episode') {
    phases = SELECTED_PHASES;
  } else {
    phases = FULL_PHASES;
  }

  // Defensive fallback: if generationStep is null (backend hasn't written it yet),
  // treat it as the first step so the first phase shows as active immediately.
  const effectiveStep = generationStep ?? Math.min(...phases[0].steps);

  // Find the active phase and its message
  let activeMessage = 'A preparar...';
  let activePhaseIndex = -1;

  for (let i = 0; i < phases.length; i++) {
    const phaseStatus = getPhaseStatus(phases[i], effectiveStep);
    if (phaseStatus === 'active') {
      activePhaseIndex = i;
      activeMessage = phases[i].message;
      break;
    }
  }

  if (activePhaseIndex === -1) {
    activePhaseIndex = 0;
    activeMessage = phases[0].message;
  }

  return (
    <div className="bg-white dark:bg-white/10 dark:backdrop-blur-md dark:border dark:border-white/15 rounded-2xl shadow-sm dark:shadow-none p-6 mb-8">
      {/* Active step message */}
      <div className="text-center mb-5">
        <p className="text-lg font-medium text-dark dark:text-white">
          {activeMessage}
        </p>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex gap-1">
          {phases.map((phase) => {
            const phaseStatus = getPhaseStatus(phase, effectiveStep);
            return (
              <div
                key={phase.id}
                className="flex-1 flex flex-col gap-1"
              >
                <div
                  className={`h-2.5 rounded-full transition-all duration-700 ${
                    phaseStatus === 'completed'
                      ? 'bg-primary'
                      : phaseStatus === 'active'
                        ? 'bg-primary animate-pulse'
                        : 'bg-gray-200 dark:bg-white/10'
                  }`}
                />
                {phases.length <= 6 && (
                  <span
                    className={`text-[10px] leading-tight text-center transition-colors duration-300 ${
                      phaseStatus === 'completed'
                        ? 'text-primary font-medium'
                        : phaseStatus === 'active'
                          ? 'text-dark dark:text-white font-medium'
                          : 'text-gray-400 dark:text-white/40'
                    }`}
                  >
                    {phase.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Time estimate + contextual notice */}
      <div className="text-center space-y-2 mt-4">
        <p className="text-sm text-gray-500 dark:text-white/60">
          Este processo pode demorar entre 1 e 3 minutos.
        </p>
        <p className="text-sm text-gray-400 dark:text-white/40">
          Podes fechar esta página — receberás um email quando estiver pronto.
        </p>
      </div>
    </div>
  );
}
